import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Share2 } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { THEME_ENGINE, bgInlineStyle, effectiveTheme } from "../lib/themeEngine";
import { BIO_THEMES } from "../lib/bioThemes";
import { BioBackground } from "../components/bio/BioBackground";
import { BioLinkCard } from "../components/bio/BioLinkCard";

// ── Asterisk brand icon ───────────────────────────────────────────────────────
function LLIcon({ color }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 2v20M2 12h20M5.636 5.636l12.728 12.728M18.364 5.636 5.636 18.364"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ── Check icon ────────────────────────────────────────────────────────────────
function CheckIcon({ color }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 12l5 5L20 7"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// First-party visitor id (per-browser, localStorage). No cross-site tracking.
function getVisitorId() {
  try {
    let v = localStorage.getItem("ll_visitor_id");
    if (!v) {
      v = crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem("ll_visitor_id", v);
    }
    return v;
  } catch {
    return null;
  }
}

export default function BioPagePublic() {
  // Served under both /bio/:slug (legacy) and the apex catch-all /:code — read
  // whichever param the matched route provided.
  const params = useParams();
  const slug = params.slug || params.code || params.handle;
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);
  const viewedRef = useRef(false); // guard StrictMode double-fire

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/bio/${slug}`);
        setPage(res.data);
        // Fire a funnel page-view once per mount
        if (!viewedRef.current) {
          viewedRef.current = true;
          api
            .post(`/bio/${slug}/view`, {
              visitor_id: getVisitorId(),
              referrer: document.referrer || null,
            })
            .catch(() => {});
        }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const trackClick = (linkItem) => {
    // Only follow http(s) destinations — block javascript:/data:/etc.
    const safe = /^https?:\/\//i.test(linkItem.url || "");
    try {
      api
        .post(`/bio/${slug}/click`, {
          link_item_id: linkItem.id,
          referrer: document.referrer || null,
          visitor_id: getVisitorId(),
        })
        .catch(() => {});
    } catch {
      /* ignore */
    }
    if (safe) window.open(linkItem.url, "_blank", "noopener,noreferrer");
  };

  const handleShare = useCallback(() => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: page?.profile_name || "Bio page", url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2200);
      });
    }
  }, [page]);

  // Loading
  if (loading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-[#1E293B]"
        data-testid="bio-public-page"
      >
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white/60" />
      </div>
    );
  }

  // 404
  if (notFound) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-paper p-6"
        data-testid="bio-public-page"
      >
        <div className="text-center">
          <p className="mb-2 font-display text-5xl font-extrabold text-ink">404</p>
          <p className="mb-5 text-sm text-ink/55">This bio page doesn't exist.</p>
          <Link to="/" className="text-sm font-semibold text-candy hover:underline">
            ← Go to LinkLane
          </Link>
        </div>
      </div>
    );
  }

  const baseTheme = THEME_ENGINE[page.theme] || BIO_THEMES[page.theme] || BIO_THEMES.minimal;
  const t = effectiveTheme(baseTheme, page.theme_custom);
  const initial = (page.profile_name || "?").charAt(0).toUpperCase();

  // Determine if theme is "dark" for icon tinting
  const isDark = ["dark", "neon", "ocean", "midnight", "aurora"].includes(page.theme);
  const iconAlpha = isDark ? "rgba(255,255,255,0.65)" : "rgba(30,41,59,0.45)";
  const iconBtnBg = isDark ? "rgba(255,255,255,0.10)" : "rgba(30,41,59,0.07)";

  // Outer scrim: slightly darken/lighten the bg to make the card pop on desktop
  const outerScrim = isDark ? "md:bg-black/25" : "md:bg-black/10";

  return (
    /*
     * 1. Full-screen wrapper — carries the theme background so the entire
     *    viewport is "in theme" (matches the Linktree desktop look).
     * 2. Inner centering layer — adds a subtle scrim on md+ so the card
     *    reads as floating.
     * 3. Card — max 390 px, rounded on desktop, full-screen on mobile.
     */
    <div
      className={`relative min-h-screen w-full overflow-hidden ${t.bg} ${t.bgAnimClass}`}
      style={bgInlineStyle(t.bgStyle, t.fontFamily)}
      data-testid="bio-public-page"
    >
      {/* Scrim + centering */}
      <div className={`flex min-h-screen flex-col items-center ${outerScrim} md:px-4 md:py-12`}>
        {/* Card */}
        <div
          className={[
            "relative flex w-full flex-col",
            // Mobile: fill screen. Desktop: floating card.
            "min-h-screen md:min-h-0",
            "md:max-w-[390px]",
            "md:overflow-hidden md:rounded-[2.5rem]",
            // Card-specific bg (same theme, re-applied so animation restarts inside)
            t.bg,
            t.bgAnimClass,
            // Subtle ring + shadow to lift it off the background
            isDark
              ? "md:shadow-[0_0_0_1px_rgba(255,255,255,0.08),0_32px_64px_rgba(0,0,0,0.55)]"
              : "md:shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_32px_64px_rgba(0,0,0,0.18)]",
          ].join(" ")}
          style={bgInlineStyle(t.bgStyle, t.fontFamily)}
        >
          {/* ── Orb background layer ─────────────────────────── */}
          <BioBackground theme={t} />

          {/* ── Top bar ──────────────────────────────────────── */}
          <div className="sticky left-0 right-0 top-0 z-20 flex items-center justify-between px-5 pb-2 pt-5">
            {/* Brand icon */}
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              title="LinkLane"
              className="flex h-9 w-9 items-center justify-center rounded-full transition-opacity hover:opacity-80"
              style={{ backgroundColor: iconBtnBg }}
            >
              <LLIcon color={iconAlpha} />
            </a>

            {/* Share */}
            <button
              type="button"
              onClick={handleShare}
              title={copied ? "Copied!" : "Share"}
              className="flex h-9 w-9 items-center justify-center rounded-full transition-opacity hover:opacity-80"
              style={{ backgroundColor: iconBtnBg }}
            >
              {copied ? (
                <CheckIcon color={iconAlpha} />
              ) : (
                <Share2 size={15} strokeWidth={2} color={iconAlpha} />
              )}
            </button>
          </div>

          {/* ── Content ──────────────────────────────────────── */}
          <motion.div
            className="flex flex-1 flex-col items-center px-6 pb-14 pt-6"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Avatar */}
            <div className="mb-4 shrink-0">
              {page.avatar_url ? (
                <img
                  src={page.avatar_url}
                  alt={page.profile_name}
                  className="h-24 w-24 rounded-full object-cover"
                  style={{ border: `3px solid ${t.accent}` }}
                />
              ) : (
                <div
                  className="flex h-24 w-24 items-center justify-center rounded-full text-3xl font-bold text-white"
                  style={{ backgroundColor: t.accent }}
                >
                  {initial}
                </div>
              )}
            </div>

            {/* Name */}
            <h1 className={`mb-1.5 text-center text-2xl font-bold leading-tight ${t.text}`}>
              {page.profile_name}
            </h1>

            {/* Bio */}
            {page.bio && (
              <p className={`mb-8 max-w-[260px] text-center text-sm leading-relaxed ${t.subtext}`}>
                {page.bio}
              </p>
            )}
            {!page.bio && <div className="mb-8" />}

            {/* Links */}
            <div className="flex w-full flex-col gap-3">
              {page.links.map((link) => (
                <BioLinkCard key={link.id} theme={t} onClick={() => trackClick(link)}>
                  <span className="flex items-center justify-center gap-2.5">
                    {link.thumbnail_url ? (
                      <img
                        src={link.thumbnail_url}
                        alt=""
                        className="h-6 w-6 shrink-0 rounded-md object-cover"
                      />
                    ) : link.icon ? (
                      <span className="shrink-0 text-base leading-none">{link.icon}</span>
                    ) : null}
                    <span className="min-w-0 truncate">{link.title}</span>
                  </span>
                </BioLinkCard>
              ))}
            </div>

            {/* Footer — PLG attribution badge (Free plans only; paid removes it).
                Links to the marketing site with UTM so bio-page signups are
                attributable. The product is the channel. */}
            <div className="mt-12">
              {(page.show_attribution ?? !page.white_label) && (
                <a
                  href="https://linklane.io/?utm_source=bio&utm_medium=powered_by&utm_campaign=plg"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] transition-opacity hover:opacity-100 ${t.subtext}`}
                  style={{ opacity: 0.55, borderColor: "currentColor" }}
                >
                  <span aria-hidden>⚡</span> Build yours with LinkLane
                </a>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── Desktop QR — hidden on mobile ──────────────────────── */}
      <div className="fixed bottom-6 right-6 z-30 hidden flex-col items-center gap-2 md:flex">
        <div
          className={[
            "flex flex-col items-center gap-2 rounded-2xl p-3",
            isDark
              ? "border border-white/15 bg-white/10 backdrop-blur-md"
              : "border-black/8 border bg-white/70 backdrop-blur-md",
          ].join(" ")}
          style={{
            boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.4)" : "0 8px 32px rgba(0,0,0,0.12)",
          }}
        >
          <div className="overflow-hidden rounded-xl bg-white p-2">
            <QRCodeCanvas
              value={typeof window !== "undefined" ? window.location.href : ""}
              size={88}
              bgColor="#ffffff"
              fgColor="#1E293B"
              level="M"
              includeMargin={false}
            />
          </div>
          <p
            className={`font-mono text-[10px] font-semibold uppercase tracking-[0.22em] ${isDark ? "text-white/50" : "text-[#1E293B]/45"}`}
          >
            View on mobile
          </p>
        </div>
      </div>
    </div>
  );
}
